import type { MessageKey } from "./ru"

export type MarketCountryCode =
  | "AE"
  | "AM"
  | "AR"
  | "AT"
  | "AU"
  | "AZ"
  | "BD"
  | "BE"
  | "BY"
  | "BR"
  | "CA"
  | "CH"
  | "CL"
  | "CO"
  | "CZ"
  | "DE"
  | "EG"
  | "ES"
  | "FR"
  | "GB"
  | "GE"
  | "ID"
  | "IN"
  | "IQ"
  | "IR"
  | "IT"
  | "JP"
  | "KE"
  | "KG"
  | "KR"
  | "KZ"
  | "LK"
  | "MX"
  | "MY"
  | "NG"
  | "NL"
  | "PE"
  | "PH"
  | "PK"
  | "PL"
  | "PT"
  | "RO"
  | "RU"
  | "SA"
  | "SE"
  | "SG"
  | "TH"
  | "TJ"
  | "TR"
  | "UA"
  | "US"
  | "UZ"
  | "VN"
  | "ZA"

export type MarketCountryAvailability = "active" | "comingSoon"

export interface MarketCountry {
  code: MarketCountryCode
  flagCode: Lowercase<MarketCountryCode>
  labelKey?: MessageKey
  sortLabel?: string
  availability: MarketCountryAvailability
  comingSoonLabel?: string
}

// Final BeepApp geography matrix: 54 product markets.
// Russia is the only active plate database at launch; the other 53 markets are visible as Coming Soon.
// Picker order and English country labels are fixed for every user and never change with UI locale.
// Each inactive row keeps its approved Coming Soon label in that market's product-primary language.
export const MARKET_COUNTRIES: readonly MarketCountry[] = [
  { code: "AR", flagCode: "ar", sortLabel: "Argentina", availability: "comingSoon", comingSoonLabel: "Próximamente" },
  { code: "AM", flagCode: "am", sortLabel: "Armenia", availability: "comingSoon", comingSoonLabel: "Շուտով" },
  { code: "AU", flagCode: "au", sortLabel: "Australia", availability: "comingSoon", comingSoonLabel: "Coming soon" },
  { code: "AT", flagCode: "at", sortLabel: "Austria", availability: "comingSoon", comingSoonLabel: "Demnächst" },
  { code: "AZ", flagCode: "az", sortLabel: "Azerbaijan", availability: "comingSoon", comingSoonLabel: "Tezliklə" },
  { code: "BD", flagCode: "bd", sortLabel: "Bangladesh", availability: "comingSoon", comingSoonLabel: "শীঘ্রই" },
  { code: "BY", flagCode: "by", sortLabel: "Belarus", availability: "comingSoon", comingSoonLabel: "Хутка" },
  { code: "BE", flagCode: "be", sortLabel: "Belgium", availability: "comingSoon", comingSoonLabel: "Binnenkort" },
  { code: "BR", flagCode: "br", sortLabel: "Brazil", availability: "comingSoon", comingSoonLabel: "Em breve" },
  { code: "CA", flagCode: "ca", sortLabel: "Canada", availability: "comingSoon", comingSoonLabel: "Coming soon" },
  { code: "CL", flagCode: "cl", sortLabel: "Chile", availability: "comingSoon", comingSoonLabel: "Próximamente" },
  { code: "CO", flagCode: "co", sortLabel: "Colombia", availability: "comingSoon", comingSoonLabel: "Próximamente" },
  { code: "CZ", flagCode: "cz", sortLabel: "Czech Republic", availability: "comingSoon", comingSoonLabel: "Již brzy" },
  { code: "EG", flagCode: "eg", sortLabel: "Egypt", availability: "comingSoon", comingSoonLabel: "قريبًا" },
  { code: "FR", flagCode: "fr", sortLabel: "France", availability: "comingSoon", comingSoonLabel: "Bientôt" },
  { code: "GE", flagCode: "ge", sortLabel: "Georgia", availability: "comingSoon", comingSoonLabel: "მალე" },
  { code: "DE", flagCode: "de", sortLabel: "Germany", availability: "comingSoon", comingSoonLabel: "Demnächst" },
  { code: "IN", flagCode: "in", sortLabel: "India", availability: "comingSoon", comingSoonLabel: "जल्द ही" },
  { code: "ID", flagCode: "id", sortLabel: "Indonesia", availability: "comingSoon", comingSoonLabel: "Segera hadir" },
  { code: "IR", flagCode: "ir", sortLabel: "Iran", availability: "comingSoon", comingSoonLabel: "به‌زودی" },
  { code: "IQ", flagCode: "iq", sortLabel: "Iraq", availability: "comingSoon", comingSoonLabel: "قريبًا" },
  { code: "IT", flagCode: "it", sortLabel: "Italy", availability: "comingSoon", comingSoonLabel: "Prossimamente" },
  { code: "JP", flagCode: "jp", sortLabel: "Japan", availability: "comingSoon", comingSoonLabel: "近日公開" },
  { code: "KZ", flagCode: "kz", sortLabel: "Kazakhstan", availability: "comingSoon", comingSoonLabel: "Жақында" },
  { code: "KE", flagCode: "ke", sortLabel: "Kenya", availability: "comingSoon", comingSoonLabel: "Hivi karibuni" },
  { code: "KG", flagCode: "kg", sortLabel: "Kyrgyzstan", availability: "comingSoon", comingSoonLabel: "Жакында" },
  { code: "MY", flagCode: "my", sortLabel: "Malaysia", availability: "comingSoon", comingSoonLabel: "Akan datang" },
  { code: "MX", flagCode: "mx", sortLabel: "Mexico", availability: "comingSoon", comingSoonLabel: "Próximamente" },
  { code: "NL", flagCode: "nl", sortLabel: "Netherlands", availability: "comingSoon", comingSoonLabel: "Binnenkort" },
  { code: "NG", flagCode: "ng", sortLabel: "Nigeria", availability: "comingSoon", comingSoonLabel: "Coming soon" },
  { code: "PK", flagCode: "pk", sortLabel: "Pakistan", availability: "comingSoon", comingSoonLabel: "جلد دستیاب" },
  { code: "PE", flagCode: "pe", sortLabel: "Peru", availability: "comingSoon", comingSoonLabel: "Próximamente" },
  { code: "PH", flagCode: "ph", sortLabel: "Philippines", availability: "comingSoon", comingSoonLabel: "Paparating na" },
  { code: "PL", flagCode: "pl", sortLabel: "Poland", availability: "comingSoon", comingSoonLabel: "Wkrótce" },
  { code: "PT", flagCode: "pt", sortLabel: "Portugal", availability: "comingSoon", comingSoonLabel: "Em breve" },
  { code: "RO", flagCode: "ro", sortLabel: "Romania", availability: "comingSoon", comingSoonLabel: "În curând" },
  { code: "RU", flagCode: "ru", sortLabel: "Russia", availability: "active" },
  { code: "SA", flagCode: "sa", sortLabel: "Saudi Arabia", availability: "comingSoon", comingSoonLabel: "قريبًا" },
  { code: "SG", flagCode: "sg", sortLabel: "Singapore", availability: "comingSoon", comingSoonLabel: "Coming soon" },
  { code: "ZA", flagCode: "za", sortLabel: "South Africa", availability: "comingSoon", comingSoonLabel: "Coming soon" },
  { code: "KR", flagCode: "kr", sortLabel: "South Korea", availability: "comingSoon", comingSoonLabel: "곧 출시" },
  { code: "ES", flagCode: "es", sortLabel: "Spain", availability: "comingSoon", comingSoonLabel: "Próximamente" },
  { code: "LK", flagCode: "lk", sortLabel: "Sri Lanka", availability: "comingSoon", comingSoonLabel: "ළඟදීම" },
  { code: "SE", flagCode: "se", sortLabel: "Sweden", availability: "comingSoon", comingSoonLabel: "Kommer snart" },
  { code: "CH", flagCode: "ch", sortLabel: "Switzerland", availability: "comingSoon", comingSoonLabel: "Demnächst" },
  { code: "TJ", flagCode: "tj", sortLabel: "Tajikistan", availability: "comingSoon", comingSoonLabel: "Ба наздикӣ" },
  { code: "TH", flagCode: "th", sortLabel: "Thailand", availability: "comingSoon", comingSoonLabel: "เร็ว ๆ นี้" },
  { code: "TR", flagCode: "tr", sortLabel: "Turkey", availability: "comingSoon", comingSoonLabel: "Yakında" },
  { code: "AE", flagCode: "ae", sortLabel: "UAE", availability: "comingSoon", comingSoonLabel: "قريبًا" },
  { code: "UA", flagCode: "ua", sortLabel: "Ukraine", availability: "comingSoon", comingSoonLabel: "Незабаром" },
  { code: "GB", flagCode: "gb", sortLabel: "United Kingdom", availability: "comingSoon", comingSoonLabel: "Coming soon" },
  { code: "US", flagCode: "us", sortLabel: "USA", availability: "comingSoon", comingSoonLabel: "Coming soon" },
  { code: "UZ", flagCode: "uz", sortLabel: "Uzbekistan", availability: "comingSoon", comingSoonLabel: "Тез орада" },
  { code: "VN", flagCode: "vn", sortLabel: "Vietnam", availability: "comingSoon", comingSoonLabel: "Sắp ra mắt" },
]

export function marketCountryLabel(country: MarketCountry, _resolveMessage: (key: MessageKey) => string): string {
  return country.sortLabel || country.code
}

// Locked Country Picker display rule:
// - active database: universal English country name;
// - inactive database: that country's own localized "Coming soon" text.
// UI locale and selected country must never affect this output.
export function marketCountryPickerLabel(country: MarketCountry): string {
  return country.availability === "active"
    ? country.sortLabel || country.code
    : country.comingSoonLabel || ""
}

export function getMarketCountriesForPicker(
  _resolveLabel: (country: MarketCountry) => string,
  _locale?: string,
): MarketCountry[] {
  return [...MARKET_COUNTRIES]
}
